import { isEmpty } from 'lodash';
import { I18n as I18nClass } from 'i18n-js';
import { registerI18n } from '@/legacy/registry';

/** Chunks por locale (code-split Vite). Regenerar con `rake i18n:export`. */
const localeModules = import.meta.glob('../locales/*.json');

/**
 * Singleton i18n-js v4 (fuente ESM; window.I18n es solo bridge legacy).
 * Las traducciones se cargan bajo demanda por locale (ver loadI18nTranslations).
 */
const i18nInstance = new I18nClass({}, {
  enableFallback: true,
  missingBehavior: 'key',
  defaultLocale: 'es',
  locale: 'es',
});

// Devolver la key tal cual cuando falta la traducción (comportamiento v3).
i18nInstance.missingTranslation.register('key', (_i18n, scope) => (
  Array.isArray(scope) ? scope.join('.') : String(scope)
));

// Compatibilidad con callers Vue/legacy que usan currentLocale().
i18nInstance.currentLocale = () => i18nInstance.locale;

/**
 * v3 usaba `{ default: ... }`; v4 usa `defaultValue`.
 * Normalizamos en el facade para no tocar ~155 call sites.
 */
function normalizeTranslateOptions(options) {
  if (!options || typeof options !== 'object') {
    return options;
  }
  if ('default' in options && !('defaultValue' in options)) {
    const { default: defaultValue, ...rest } = options;
    return { ...rest, defaultValue };
  }
  return options;
}

/**
 * Sincroniza el singleton con window.I18n para JST legacy y classic-scope.
 */
export function syncWindowI18n() {
  registerI18n(i18nInstance);
}

syncWindowI18n();

export function getI18n() {
  return i18nInstance;
}

/** @deprecated Usar `t()` o `currentLocale()`; solo `getI18n()` si necesitas la instancia. */
export const I18n = i18nInstance;

export function t(scope, options) {
  return i18nInstance.t(scope, normalizeTranslateOptions(options));
}

export function currentLocale() {
  return i18nInstance.locale;
}

export function booleanToHuman(valor) {
  let key = valor?.toString();
  if (isEmpty(key)) {
    key = 'nil';
  }
  return t(`boolean.${key}`);
}

export function humanizarEnum(model, enumName, enumValue) {
  if (!enumName) {
    return undefined;
  }

  return t(
    `activerecord.enums.${model}.${enumName}.${enumValue}`,
    { defaultValue: enumValue },
  );
}

// @deprecated Usar humanizarEnum
export default function humanEnumName(model, enumName, enumValue) {
  return humanizarEnum(model, enumName, enumValue);
}

export function configureLocaleFromMeta() {
  const defaultLocale = document.querySelector('meta[name=default-locale]')?.content;
  const locale = document.querySelector('meta[name=locale]')?.content
    || document.documentElement.lang;

  if (defaultLocale) {
    i18nInstance.defaultLocale = defaultLocale;
  }
  if (locale) {
    // "va" no tiene YAML propio: Rails lo mapea a "ca".
    i18nInstance.locale = locale === 'va' ? 'ca' : locale;
  }

  syncWindowI18n();
}

function resolveLocale() {
  const raw = document.querySelector('meta[name=locale]')?.content
    || document.documentElement.lang
    || i18nInstance.defaultLocale
    || 'es';
  // "va" → "ca" (Config::Locale.change_locale).
  return raw === 'va' ? 'ca' : raw;
}

/**
 * Carga el JSON del locale activo vía import dinámico (code-split Vite).
 * Mantiene el await en boot_application / boot_public.
 */
export async function loadI18nTranslations() {
  configureLocaleFromMeta();

  const locale = resolveLocale();
  const loader = localeModules[`../locales/${locale}.json`];
  if (!loader) {
    console.error(`[cifra] Locale i18n no disponible: ${locale}`);
    return;
  }

  try {
    const translations = (await loader()).default;
    i18nInstance.store(translations);
    i18nInstance.locale = locale;
    syncWindowI18n();
  } catch (err) {
    console.error('[cifra] No se pudieron cargar traducciones i18n', err);
  }
}
